/**
 * Top-level route tree.
 *
 * /login         → public login page
 * /              → home / tile picker
 * /dashboard/*   → OakAllow admin dashboard
 * /ashkeep/*     → AshKeep governance
 * /finance/*     → Finance
 *
 * Every page is lazy-loaded so the initial bundle only contains the shell +
 * whatever page the user landed on. Combined with manualChunks in
 * vite.config.ts (react, recharts, lucide-react as separate vendor chunks),
 * this keeps the entry JS small and lets Cloudflare cache the vendor chunks
 * independently from app code.
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import RequireAuth from './components/RequireAuth';
import DashboardShell from './components/DashboardShell';
import AshKeepShell from './components/AshKeepShell';
import FinanceShell from './components/FinanceShell';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const HomePage = lazy(() => import('./pages/HomePage'));

// Dashboard pages
const OverviewPage = lazy(() => import('./pages/dashboard/OverviewPage'));
const DevelopersPage = lazy(() => import('./pages/dashboard/DevelopersPage'));
const DeveloperDetailPage = lazy(() => import('./pages/dashboard/developers/DeveloperDetailPage'));
const PartnersPage = lazy(() => import('./pages/dashboard/PartnersPage'));
const OakGuardPage = lazy(() => import('./pages/dashboard/OakGuardPage'));
const PartnerDetailPage = lazy(() => import('./pages/dashboard/PartnerDetailPage'));
const AuthoritiesPage = lazy(() => import('./pages/dashboard/AuthoritiesPage'));
const AuthorityDetailPage = lazy(() => import('./pages/dashboard/AuthorityDetailPage'));
const IntegrationHealthPage = lazy(() => import('./pages/dashboard/IntegrationHealthPage'));
const ActivityPage = lazy(() => import('./pages/dashboard/ActivityPage'));
const RefPathPage = lazy(() => import('./pages/dashboard/RefPathPage'));
const AuditLogPage = lazy(() => import('./pages/dashboard/AuditLogPage'));
const BacklogPage = lazy(() => import('./pages/dashboard/BacklogPage'));
const BillingPage = lazy(() => import('./pages/dashboard/BillingPage'));
const CleanupPage = lazy(() => import('./pages/dashboard/CleanupPage'));
const AgeOffPage = lazy(() => import('./pages/dashboard/AgeOffPage'));
const EmailHistoryPage = lazy(() => import('./pages/dashboard/EmailHistoryPage'));
const AccountDeletionPage = lazy(() => import('./pages/dashboard/AccountDeletionPage'));
const CouponsPage = lazy(() => import('./pages/dashboard/CouponsPage'));
const RetentionPage = lazy(() => import('./pages/dashboard/RetentionPage'));
const ReviewersPage = lazy(() => import('./pages/dashboard/ReviewersPage'));
const D1Page = lazy(() => import('./pages/dashboard/D1Page'));
const DeployPage = lazy(() => import('./pages/dashboard/deploy/DeployPage'));
const DirectEmailPage = lazy(() => import('./pages/dashboard/DirectEmailPage'));
const FeedbackPage = lazy(() => import('./pages/dashboard/FeedbackPage'));
const BeechHubPage = lazy(() => import('./pages/dashboard/BeechHubPage'));
const BlogPostsPage = lazy(() => import('./pages/dashboard/blog/BlogPostsPage'));
const BlogPostEditPage = lazy(() => import('./pages/dashboard/blog/BlogPostEditPage'));
const BlogMediaPage = lazy(() => import('./pages/dashboard/blog/BlogMediaPage'));
const RecipesPage = lazy(() => import('./pages/dashboard/recipes/RecipesPage'));
const RecipeEditPage = lazy(() => import('./pages/dashboard/recipes/RecipeEditPage'));
const ScreenshotsPage = lazy(() => import('./pages/dashboard/ScreenshotsPage'));
const SsoConfigPage = lazy(() => import('./pages/dashboard/sso/SsoConfigPage'));
const TicketsPage = lazy(() => import('./pages/dashboard/tickets/TicketsPage'));
const TicketDetailPage = lazy(() => import('./pages/dashboard/tickets/TicketDetailPage'));
const TrafficPage = lazy(() => import('./pages/dashboard/TrafficPage'));
const RunbooksPage = lazy(() => import('./pages/dashboard/runbooks/RunbooksPage'));
const RunbookEditPage = lazy(() => import('./pages/dashboard/runbooks/RunbookEditPage'));
const RunbookColumnsPage = lazy(() => import('./pages/dashboard/runbooks/RunbookColumnsPage'));

// AshKeep pages
const AshKeepOverviewPage = lazy(() => import('./pages/ashkeep/OverviewPage'));
const AshKeepUsersPage = lazy(() => import('./pages/ashkeep/users/UsersPage'));
const AshKeepUserDetailPage = lazy(() => import('./pages/ashkeep/users/UserDetailPage'));
const AshKeepOrganizationsPage = lazy(() => import('./pages/ashkeep/OrganizationsPage'));
const TeamsPage = lazy(() => import('./pages/dashboard/teams/TeamsPage'));
const TeamDetailPage = lazy(() => import('./pages/dashboard/teams/TeamDetailPage'));
const AshKeepToolsPage = lazy(() => import('./pages/ashkeep/ToolsPage'));
const AshKeepTestingPage = lazy(() => import('./pages/ashkeep/TestingPage'));
const AshKeepApprovalsPage = lazy(() => import('./pages/ashkeep/ApprovalsPage'));
const AshKeepManifestsPage = lazy(() => import('./pages/ashkeep/ManifestsPage'));
const AshKeepSettingsPage = lazy(() => import('./pages/ashkeep/SettingsPage'));
const AshKeepD1Page = lazy(() => import('./pages/ashkeep/D1Page'));

// Finance pages
const FinanceOverviewPage = lazy(() => import('./pages/finance/OverviewPage'));
const FinanceTransactionsPage = lazy(() => import('./pages/finance/TransactionsPage'));
const FinanceCouponsPage = lazy(() => import('./pages/finance/CouponsPage'));
const FinanceStripePage = lazy(() => import('./pages/finance/StripePage'));
const FinanceRevenuePage = lazy(() => import('./pages/finance/RevenuePage'));

function PageLoading() {
  return (
    <div style={{ padding: '1.5rem', color: 'var(--text-tertiary)', fontSize: '0.875rem' }}>
      Loading…
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          <Route element={<RequireAuth />}>
            <Route path="/" element={<HomePage />} />

            <Route element={<DashboardShell />}>
              <Route path="/dashboard" element={<OverviewPage />} />
              <Route path="/dashboard/teams" element={<TeamsPage />} />
              <Route path="/dashboard/teams/:id" element={<TeamDetailPage />} />
              <Route path="/dashboard/developers" element={<DevelopersPage />} />
              <Route path="/dashboard/developers/:id" element={<DeveloperDetailPage />} />
              <Route path="/dashboard/partners" element={<PartnersPage />} />
              <Route path="/dashboard/partners/:id" element={<PartnerDetailPage />} />
              <Route path="/dashboard/authorities" element={<AuthoritiesPage />} />
              <Route path="/dashboard/oakguard" element={<OakGuardPage />} />
              <Route path="/dashboard/authorities/:id" element={<AuthorityDetailPage />} />
              <Route path="/dashboard/integration-health" element={<IntegrationHealthPage />} />
              <Route path="/dashboard/activity" element={<ActivityPage />} />
              <Route path="/dashboard/ref" element={<RefPathPage />} />
              <Route path="/dashboard/audit-log" element={<AuditLogPage />} />
              <Route path="/dashboard/backlog" element={<BacklogPage />} />
              <Route path="/dashboard/billing" element={<BillingPage />} />
              <Route path="/dashboard/cleanup" element={<CleanupPage />} />
              <Route path="/dashboard/age-off" element={<AgeOffPage />} />
              <Route path="/dashboard/email-history" element={<EmailHistoryPage />} />
              <Route path="/dashboard/account-deletion" element={<AccountDeletionPage />} />
              <Route path="/dashboard/coupons" element={<CouponsPage />} />
              <Route path="/dashboard/retention" element={<RetentionPage />} />
              <Route path="/dashboard/reviewers" element={<ReviewersPage />} />
              <Route path="/dashboard/d1" element={<D1Page />} />
              <Route path="/dashboard/deploy" element={<DeployPage />} />
              <Route path="/dashboard/direct-email" element={<DirectEmailPage />} />
              <Route path="/dashboard/feedback" element={<FeedbackPage />} />
              <Route path="/dashboard/beech" element={<BeechHubPage />} />
              <Route path="/dashboard/blog" element={<BlogPostsPage />} />
              <Route path="/dashboard/blog/media" element={<BlogMediaPage />} />
              <Route path="/dashboard/blog/:id" element={<BlogPostEditPage />} />
              <Route path="/dashboard/recipes" element={<RecipesPage />} />
              <Route path="/dashboard/recipes/:id" element={<RecipeEditPage />} />
              <Route path="/dashboard/screenshots" element={<ScreenshotsPage />} />
              <Route path="/dashboard/sso" element={<SsoConfigPage />} />
              <Route path="/dashboard/tickets" element={<TicketsPage />} />
              <Route path="/dashboard/tickets/:id" element={<TicketDetailPage />} />
              <Route path="/dashboard/traffic" element={<TrafficPage />} />
              <Route path="/dashboard/runbooks" element={<RunbooksPage />} />
              <Route path="/dashboard/runbooks/_columns" element={<RunbookColumnsPage />} />
              <Route path="/dashboard/runbooks/:platform/:type" element={<RunbookEditPage />} />
            </Route>

            <Route element={<AshKeepShell />}>
              <Route path="/ashkeep" element={<AshKeepOverviewPage />} />
              <Route path="/ashkeep/users" element={<AshKeepUsersPage />} />
              <Route path="/ashkeep/users/:id" element={<AshKeepUserDetailPage />} />
              <Route path="/ashkeep/organizations" element={<AshKeepOrganizationsPage />} />
              <Route path="/ashkeep/tools" element={<AshKeepToolsPage />} />
              <Route path="/ashkeep/testing" element={<AshKeepTestingPage />} />
              <Route path="/ashkeep/approvals" element={<AshKeepApprovalsPage />} />
              <Route path="/ashkeep/manifests" element={<AshKeepManifestsPage />} />
              <Route path="/ashkeep/settings" element={<AshKeepSettingsPage />} />
              <Route path="/ashkeep/d1" element={<AshKeepD1Page />} />
            </Route>

            <Route element={<FinanceShell />}>
              <Route path="/finance" element={<FinanceOverviewPage />} />
              <Route path="/finance/transactions" element={<FinanceTransactionsPage />} />
              <Route path="/finance/coupons" element={<FinanceCouponsPage />} />
              <Route path="/finance/stripe" element={<FinanceStripePage />} />
              <Route path="/finance/revenue" element={<FinanceRevenuePage />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
